import { compact } from "lodash";
import { MessageIDS } from "../i18n/util";
import { ApplicationStateEnum, DeviceEnum, PatchApplicantDetailsResponseLanguageEnum } from "../network/APITypes";
import { TableHeaderOption } from "../stores/TableStore";
import { THEME, isDal } from "../theme/ThemeStore";
import {
    applicantListHeaderEnum,
    DateRangeEnum,
    DiscoveryEnum,
    DocumentsEnum,
    IApplicationStateEnum,
    JobCategoryEnum,
    ShirtSizeEnum,
    TemplateEnum
} from "../types/models";

export type Option<T> = {
    messageId: MessageIDS;
    value: T;
};

export type NestedOption<T> = {
    messageId: MessageIDS;
    options: Array<Option<T>>;
};

export const deviceOptions: Option<DeviceEnum>[] = [
    { messageId: "screen.details.macbook", value: "macbook" },
    { messageId: "screen.details.windowsLaptop", value: "windows_laptop" },
    { messageId: "screen.details.linuxLaptop", value: "linux_laptop" }
];

export const languageOptions: Option<PatchApplicantDetailsResponseLanguageEnum>[] = [
    { messageId: "screen.details.german", value: "de" },
    { messageId: "screen.details.english", value: "en" }
];

export const abbreviatedLanguageOptions: Option<PatchApplicantDetailsResponseLanguageEnum>[] = [
    { messageId: "screen.dashboard.dialog.german", value: "de" },
    { messageId: "screen.dashboard.dialog.english", value: "en" }
];

export const discoveryOptions: Option<DiscoveryEnum>[] = [
    { messageId: "screen.personalData.google", value: "google" },
    { messageId: "screen.personalData.school", value: "school" },
    { messageId: "screen.personalData.karriereAt", value: "karriere_at" },
    { messageId: "screen.personalData.recommendation", value: "recommendation" },
    { messageId: "screen.personalData.devjobs", value: "devjobs" },
    { messageId: "screen.personalData.linkedin", value: "linkedin" },
    { messageId: "screen.personalData.differentDiscoverySource", value: "different_source" }
];

export const getJobCategoryOptions = (): Option<JobCategoryEnum>[] =>
    THEME.THEME_NAME === "dal"
        ? [
              {
                  messageId: "screen.personalData.dal.frontend",
                  value: "dal_frontend"
              },
              // disable team lead web development https://a3.slack.com/archives/C011N663T08/p1709638715609029?thread_ts=1709637138.422689&cid=C011N663T08
              //   {
              //       messageId: "screen.personalData.dal.web",
              //       value: "dal_web_dev"
              //   },
              {
                  messageId: "screen.personalData.dal.fullStack",
                  value: "dal_full_stack"
              },
              {
                  messageId: "screen.personalData.dal.backend",
                  value: "dal_backend"
              },
              {
                  messageId: "screen.personalData.dal.uiux",
                  value: "dal_ui_ux"
              },
              {
                  messageId: "screen.personalData.dal.graphic",
                  value: "dal_graphic"
              },
              {
                  messageId: "screen.personalData.dal.motionDesign",
                  value: "dal_motion_design"
              },
              {
                  messageId: "screen.personalData.dal.projectManagement",
                  value: "dal_project_management"
              },
              {
                  messageId: "screen.personalData.dal.contentCreation",
                  value: "dal_content_creation"
              },
              {
                  messageId: "screen.personalData.dal.unsolicited",
                  value: "dal_unsolicited"
              },
              {
                  messageId: "screen.personalData.dal.internship",
                  value: "dal_apprentice"
              },
              {
                  messageId: "screen.personalData.dal.creativeDirector",
                  value: "dal_creative_director"
              }
          ]
        : [
              // disable apprentice
              //   {
              //       messageId: "screen.personalData.apprenticeship",
              //       value: "apprentice"
              //   },
              {
                  messageId: "screen.personalData.dualStudy",
                  value: "dual_study"
              },
              {
                  messageId: "screen.personalData.projectManager",
                  value: "project_management"
              },
              {
                  messageId: "screen.personalData.androidDeveloper",
                  value: "android"
              },
              {
                  messageId: "screen.personalData.iosDeveloper",
                  value: "ios"
              },
              {
                  messageId: "screen.personalData.typescriptFrontendEngineer",
                  value: "web"
              },
              {
                  messageId: "screen.personalData.goBackendDevOpsEngineer",
                  value: "backend"
              },
              {
                  messageId: "screen.personalData.intern",
                  value: "trainee"
              },
              {
                  messageId: "screen.personalData.flutter",
                  value: "flutter"
              },
              { messageId: "screen.personalData.qa", value: "qa" },
              { messageId: "screen.personalData.unsolicited", value: "unsolicited" }
          ];

const getCoreStatusOptions = (): Option<ApplicationStateEnum>[] =>
    THEME.THEME_NAME === "dal"
        ? [
              { messageId: "screen.dashboard.firstInterview", value: "first_interview" },
              { messageId: "screen.dashboard.secondInterview", value: "second_interview" },
              { messageId: "screen.dashboard.accepted", value: "hired" },
              { messageId: "screen.dashboard.rejected", value: "denied" }
          ]
        : [
              { messageId: "screen.dashboard.codeExample", value: "coding_example" },
              { messageId: "screen.dashboard.firstInterview", value: "first_interview" },
              { messageId: "screen.dashboard.secondInterview", value: "second_interview" },
              { messageId: "screen.dashboard.accepted", value: "hired" },
              { messageId: "screen.dashboard.rejected", value: "denied" }
          ];

export const getKanbanStatusOptions = (): Option<ApplicationStateEnum>[] => [
    { messageId: "screen.dashboard.applied", value: "application_received" },
    ...getCoreStatusOptions()
];

// options for the descriptions of the templates. Function to fill actual templates can be found in helpers.ts templateToString

export const nestedTemplateOptions = (): Array<NestedOption<TemplateEnum> | Option<TemplateEnum>> => {
    return compact([
        isDal()
            ? null
            : {
                  messageId: "template.codeExample.title",
                  options: [
                      {
                          messageId: "template.codeExample.information.android.title",
                          value: "codeExample_information_android"
                      },
                      { messageId: "template.codeExample.information.ios.title", value: "codeExample_information_ios" },
                      {
                          messageId: "template.codeExample.information.frontend.junior.title",
                          value: "codeExample_information_frontend_junior"
                      },
                      {
                          messageId: "template.codeExample.information.frontend.regular.title",
                          value: "codeExample_information_frontend_regular"
                      },
                      {
                          messageId: "template.codeExample.information.backend.title",
                          value: "codeExample_information_backend"
                      },
                      {
                          messageId: "template.codeExample.information.flutter.title",
                          value: "codeExample_information_flutter"
                      },
                      {
                          messageId: "template.codeExample.information.allteams.title",
                          value: "codeExample_information_allteams"
                      }
                  ]
              },
        {
            messageId: "template.firstInterview.title",
            options: [
                { messageId: "template.firstInterview.application.title", value: "firstInterview" },
                { messageId: "template.firstInterview.application.online.title", value: "firstInterview_online" },
                { messageId: "template.firstInterview.codeExample.title", value: "firstInterview_codeExample" },
                {
                    messageId: "template.firstInterview.codeExample.online.title",
                    value: "firstInterview_codeExample_online"
                }
            ]
        },
        {
            messageId: "template.secondInterview.title",
            options: [
                { messageId: "template.secondInterview.invitation.title", value: "secondInterview" },
                { messageId: "template.secondInterview.invitation.online.title", value: "secondInterview_online" }
            ]
        },
        { messageId: "template.hired.title", value: "hired" },
        {
            messageId: "template.rejection.title",
            options: [
                {
                    value: "rejection_general",
                    messageId: "template.rejection.general.title"
                },
                { messageId: "template.rejection.evidence.title", value: "rejection_evidence" },
                { messageId: "template.rejection.abroad.title", value: "rejection_abroad" },
                { messageId: "template.rejection.study.title", value: "rejection_study" },
                { messageId: "template.rejection.apprenticeship.title", value: "rejection_apprenticeship" },
                { messageId: "template.rejection.internship.title", value: "rejection_internship" },
                { messageId: "template.rejection.internship.evidence.title", value: "rejection_internship_evidence" },
                { messageId: "template.codeExample.rejection.evidence.title", value: "codeExample_rejection_evidence" },
                { messageId: "template.codeExample.rejection.feedback.title", value: "codeExample_rejection_feedback" },
                { messageId: "template.codeExample.rejection.general.title", value: "codeExample_rejection_general" },
                { messageId: "template.rejection.firstInterview.title", value: "rejection_first_interview" },
                {
                    messageId: "template.rejection.firstInterview.evidence.title",
                    value: "rejection_first_interview_evidence"
                }
            ]
        }
    ]);
};

export const allStatusOptions: Option<IApplicationStateEnum>[] = [
    { messageId: "screen.dashboard.applied", value: "application_received" },
    ...getCoreStatusOptions(),
    { messageId: "screen.dashboard.evidence", value: "evidence" }
];

export const shirtSizeOptions: Option<ShirtSizeEnum>[] = [
    { messageId: "screen.applicantDetails.XS", value: "XS" },
    { messageId: "screen.applicantDetails.S", value: "S" },
    { messageId: "screen.applicantDetails.M", value: "M" },
    { messageId: "screen.applicantDetails.L", value: "L" },
    { messageId: "screen.applicantDetails.XL", value: "XL" },
    { messageId: "screen.applicantDetails.XXL", value: "XXL" }
];

const coreDateRangeOptions: Option<DateRangeEnum>[] = [
    { messageId: "screen.evidence.lastThreeMonths", value: 3 },
    { messageId: "screen.evidence.lastSixMonths", value: 6 }
];

export const evidenceDateRangeOptions: Option<DateRangeEnum>[] = [
    ...coreDateRangeOptions,
    { messageId: "screen.evidence.lastTwelveMonths", value: 12 },
    { messageId: "screen.evidence.over12Months", value: undefined }
];

export const rejectedDateRangeOptions: Option<DateRangeEnum>[] = [
    ...coreDateRangeOptions,
    { messageId: "screen.evidence.lastNineMonths", value: 9 }
];

export const documentOptions: Option<DocumentsEnum>[] = [
    {
        messageId: "screen.dataUpload.resume",
        value: "resume"
    },
    {
        messageId: "screen.dataUpload.video",
        value: "video"
    },
    {
        messageId: "screen.dataUpload.portfolio",
        value: "portfolio"
    },
    {
        messageId: "screen.dataUpload.photo",
        value: "photo"
    },
    {
        messageId: "screen.dataUpload.motivationalLetter",
        value: "motivationalLetter"
    },
    {
        messageId: "screen.dashboard.dialog.contract",
        value: "contract"
    },
    {
        messageId: "screen.dataUpload.optionalFile",
        value: "optionalFiles"
    }
];

export const evidenceListHeaderOptions: TableHeaderOption<applicantListHeaderEnum>[] = [
    { messageId: "screen.verify.personalData.name", value: "name" },
    { messageId: "screen.dashboard.division", value: "division", sort: false },
    { messageId: "screen.evidence.salary", value: "salary" },
    { messageId: "screen.evidence.lastChange", value: "lastChange" }
];

export const dashboardListHeaderOptions: TableHeaderOption<applicantListHeaderEnum>[] = [
    { messageId: "screen.verify.personalData.name", value: "name" },
    { messageId: "screen.dashboard.division", value: "division", sort: false },
    { messageId: "screen.dashboard.status", value: "status", sort: false },
    { messageId: "screen.evidence.salary", value: "salary" },
    { messageId: "screen.evidence.lastChange", value: "lastChange" },
    { messageId: "screen.dashboard.applicationDate", value: "createdAt" },
    { messageId: "screen.dashboard.actions", value: "actions" }
];

/* PHONE NUMBER REGEX */

export const phoneRegex = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

//#region mock data

//#endregion
